import { LogCategory } from "Enumerations";
import { useCallback } from "react";
import { useRootStore } from "Store/RootStore";

import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export const useLogging = (componentCategory: LogCategory) => {
    const appInsights = useAppInsightsContext();
    const logStore = useRootStore("LogStore");

    const trackTrace = useCallback(
        (tagId: string, message?: string, properties?: StringMap, ...optionalParams: any[]) => {
            const appInsightproperties = getStandardPropertiesMap(tagId, componentCategory, properties, optionalParams);
            message = message ? message.substring(0, 1024) : "";
            appInsights.trackTrace({ message }, appInsightproperties);
        },
        [appInsights, componentCategory]
    );

    const trackException = useCallback(
        (tagId: string, error: any, message?: string, properties?: StringMap, ...optionalParams: any[]) => {
            const appInsightproperties = getStandardPropertiesMap(tagId, componentCategory, properties, message, optionalParams);
            console.error(error, appInsightproperties, optionalParams);
            appInsights.trackException({ exception: error, properties: appInsightproperties });
            logStore.lastErrorCorrelationId = properties?.CorrelationID ?? "";
        },
        [appInsights, componentCategory, logStore]
    );

    const trackEvent = useCallback(
        (tagId: string, eventName: string, data?: any) => {
            const appInsightproperties = getStandardPropertiesMap(tagId, componentCategory, {});
            appInsights.trackEvent({ name: eventName, properties: appInsightproperties }, data);
        },
        [appInsights, componentCategory]
    );

    const startTrackEvent = useCallback(
        (eventName: string) => {
            const insights = appInsights.getAppInsights();
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!insights) {
                console.warn("AppInsights not initialized properly.");
                return;
            }
            insights.startTrackEvent(eventName);
        },
        [appInsights]
    );

    const stopTrackEvent = useCallback(
        (eventName: string, data?: any) => {
            const insights = appInsights.getAppInsights();
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!insights) {
                console.warn("AppInsights not initialized properly.");
                return;
            }
            const appInsightproperties = getStandardPropertiesMap("", componentCategory, {});
            insights.stopTrackEvent(eventName, appInsightproperties, data);
        },
        [appInsights, componentCategory]
    );

    return {
        trackTrace,
        trackException,
        trackEvent,
        startTrackEvent,
        stopTrackEvent
    };
};

type StringMap = { [x: string]: string };

const getStandardPropertiesMap = (tagId: string, componentCategory: LogCategory, properties?: StringMap, ...optionalParams: any[]): StringMap => {
    let map: StringMap = {};
    if (properties) {
        map = properties;
    }
    const tagIdKey: string = "TagId";
    const logCategoryKey: string = "Category";
    const pageUrlKey: string = "PageUrl";
    const logLayerKey: string = "LogLayer";
    map[tagIdKey] = tagId;
    map[logCategoryKey] = LogCategory[componentCategory];
    map[pageUrlKey] = window.location.href;
    map[logLayerKey] = "Client";
    optionalParams = optionalParams.flat(2);
    if (optionalParams.length > 0) {
        let value: string = "";
        optionalParams.forEach((element) => {
            if (element) {
                value = value + JSON.stringify(element).replace(/^",/, '",\r\n');
            }
        });
        const messageKey = "AdditionalParams";
        map[messageKey] = value;
    }
    return map;
};
